import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const LaserDentistry = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Laser Dentistry Fort Worth TX | Advanced Dental Laser Treatment | Fort Worth Dental</title>
        <meta name="description" content="Advanced laser dentistry in Fort Worth TX. Precise, comfortable dental treatments with minimal pain and faster healing. LANAP, soft tissue laser. Call (817) 985-3337." />
        <meta name="keywords" content="laser dentistry fort worth, dental laser treatment fort worth tx, LANAP laser, soft tissue laser, painless dentistry" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/dentist-examining.jpg)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Laser Dentistry in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Advanced Technology for Comfortable Treatment</p>
            <p className="text-sm mt-4 opacity-90">Precise | Gentle | Fast Healing</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is Laser Dentistry */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/laser-new-1.jpg" 
                  alt="Laser dentistry treatment Fort Worth TX - Advanced dental laser technology for precise, minimally invasive procedures" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examining.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Is Laser Dentistry?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  In laser dentistry, your dentist is trained and certified to use the intense beams of lights that are emitted by dental lasers to perform a variety of treatments in a way that is more precise and gentler than traditional dental practices. This technology is quickly becoming the preferred treatment to deal with a great variety of dental procedures.
                </p>
              </div>
            </div>
          </section>

          {/* What Are The Benefits Of Laser Dentistry */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Are The Benefits Of Laser Dentistry?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  The reason that laser dentistry is so rapidly increasing in popularity is that it offers several crucial and desirable benefits. One benefit is the reduction of bleeding during the actual treatment and also the reduction of bleeding after the procedure is completed. The light beams of laser dentistry are also much gentler than many of the other tools that dentists have traditionally used. This allows tissue to heal much more quickly after a completed procedure. This, of course, allows recovery times to be much shorter.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  A laser beam is also much more precise than other methods. This means that there is a great reduction in pain and discomfort during the procedure. Greater precision also contributes to a shortened recovery time following the procedure.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Another benefit to laser dentistry is that the reduced contact of the mouth with the tools and hands of the dentist can reduce the possibility of bacterial infections.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/laser-new-2.jpg" 
                  alt="Benefits of laser dental treatment - Faster healing, less pain, and improved precision compared to traditional methods" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* What Are Some Examples Of The Use Of Laser Dentistry */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/laser-new-1.jpg" 
                  alt="Dental laser applications - Soft tissue surgery, cavity detection, and gum contouring at Fort Worth Dental" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examining.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Are Some Examples Of The Use Of Laser Dentistry?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Laser dentistry is proving to be effective in a growing number of dental treatments. These possibilities can range from cosmetic treatments to preventative treatments. It seems very likely that the use of laser dentistry will continue to develop and be used in more and more situations.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Lasers are used in professional teeth-whitening sessions in order to stimulate the whitening agent in the procedure. Lasers can be used to eliminate certain tumors from soft tissue in the mouth. Lasers can replace the dreaded drill when it comes time for a filling. Lasers can also be used to deal with gum tissues in a variety of gum treatments.
                </p>
              </div>
            </div>
          </section>

          {/* Key Benefits Summary */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Key Benefits of Laser Dentistry</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-teal-600 text-3xl mb-3">✓</div>
                <h3 className="font-semibold text-xl mb-2">Reduced Bleeding</h3>
                <p className="text-gray-600">Less bleeding during and after procedures compared to traditional methods.</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-teal-600 text-3xl mb-3">✓</div>
                <h3 className="font-semibold text-xl mb-2">Faster Healing</h3>
                <p className="text-gray-600">Gentler treatment allows tissue to heal much more quickly.</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-teal-600 text-3xl mb-3">✓</div>
                <h3 className="font-semibold text-xl mb-2">Greater Precision</h3>
                <p className="text-gray-600">Laser beams are much more precise than traditional dental tools.</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-teal-600 text-3xl mb-3">✓</div>
                <h3 className="font-semibold text-xl mb-2">Less Pain</h3>
                <p className="text-gray-600">Significant reduction in pain and discomfort during procedures.</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-teal-600 text-3xl mb-3">✓</div>
                <h3 className="font-semibold text-xl mb-2">Shorter Recovery</h3>
                <p className="text-gray-600">Recovery times are much shorter than with traditional methods.</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-teal-600 text-3xl mb-3">✓</div>
                <h3 className="font-semibold text-xl mb-2">Reduced Infection Risk</h3>
                <p className="text-gray-600">Less contact reduces the possibility of bacterial infections.</p>
              </div>
            </div>
          </section>

          {/* Laser Dentistry Applications */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Applications of Laser Dentistry</h2>
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span className="text-lg">Professional teeth whitening treatments</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span className="text-lg">Removal of soft tissue tumors</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span className="text-lg">Cavity preparation and fillings</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span className="text-lg">Gum disease treatments</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span className="text-lg">Gum reshaping and contouring</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span className="text-lg">Crown lengthening procedures</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Laser Dentistry?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we use state-of-the-art laser technology to provide minimally invasive, comfortable treatment options. 
              Serving Fort Worth, Aledo, Benbrook, and surrounding areas.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Advanced Technology</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Experience the Benefits of Laser Dentistry</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about our laser dentistry services in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Laser Dentistry Consultation in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default LaserDentistry;
